import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react'
import Reflux from 'reflux-react-16'

import SearchBar from '../SearchBar/index.js'
import Table, {TableColumn, TableRef} from '../UI/table/index.js'
import {Button, Colors, Panel, Popup, Row, Stack} from '../UI/index.js'
import {formSpacing} from '../UI/forms/index.js'
import RouteStatusBadge from './RouteStatusBadge.js'
import EditRouteModal from './EditRouteModal.js'
import {executedTitle, getRouteExportColumns, getRouteRow, plannedTitle, RouteRow} from './routeSummary.js'
import ExportTemplateModal from '../export/ExportTemplateModal.js'
import runRouteBulkAction, {routeBulkActionOptions} from './runRouteBulkAction.js'
import {getRouteBulkActionDisabledReason, RouteBulkAction} from '../../utils/routeBulkActions.js'
import OrderStore from '../../stores/OrderStore.js'
import UserStore from '../../stores/UserStore.js'
import OrderActions from '../../actions/OrderActions.js'
import useVehicleStore from '../../stores/VehicleStore.js'
import filter from '../../utils/filter.js'
import csvExport from '../../utils/csvExport.js'
import branchesAvailable from '@utils/branchesAvailable.js'
import moment from 'moment'
import t from '../../helpers/Languages.js'
import type {PopupRef} from '../UI/overlays/Popup.js'
import type {Query} from '../SearchBar/SearchItem.js'
import type {FrontEndRoute} from '../../../server/functions/route/route.types.js'
import type {Reseller} from '../../../server/functions/resellers/resellers.types.js'
import type {User} from '../../../server/functions/users/users.types.js'

interface RoutesTableProps {
    reseller: Reseller
    /** Saves the setting at path, for the export templates */
    onUpdateReseller: (reseller: Reseller, path: string) => void
    // From react-router
    history: {push: (path: string) => void}
    // From the OrderStore: the routes of its date range (orders.get, for the selected branches)
    routes: Record<string, FrontEndRoute>
    orderStartDate: string
    orderEndDate: string
    ordersLoading: boolean
    // From the UserStore
    usersObject: Record<string, User>
}

// On the time only, not the date; routes without the time last (an empty sort value)
const timeSortValue = (time: string) => time || undefined

// EditRouteModal is JS: typed for what is used here
type EditRouteModalRef = {open: () => void}
const EditRoute = EditRouteModal as unknown as React.ForwardRefExoticComponent<{reseller: Reseller, users: Record<string, User>} & React.RefAttributes<EditRouteModalRef>>

const newRouteExportTemplate = {name: '', columns: ['route.name', 'route.date', 'route.messenger'], method: 'route'}

// The other bulk actions are left to the Dagplanning
const tableBulkActions: RouteBulkAction[] = ['reschedule', 'remove']

/** The routes of a date range in a table, the Routes tab of /routes. Shares its dates with the Ritten view. */
function RoutesTable({reseller, onUpdateReseller, history, routes, orderStartDate, orderEndDate, ordersLoading, usersObject}: RoutesTableProps) {
    const vehicles = useVehicleStore((state) => state.vehicles)
    const getVehicles = useVehicleStore((state) => state.getVehicles)
    const [queries, setQueries] = useState<Query[]>([])
    const [selectedRoutes, setSelectedRoutes] = useState<string[]>([])
    const tableRef = useRef<TableRef>(null)
    const popupRef = useRef<PopupRef>(null)
    const editRouteModalRef = useRef<EditRouteModalRef>(null)
    const [bulkRunning, setBulkRunning] = useState(false)
    const [exportOpen, setExportOpen] = useState(false)

    // Read by the click on a row: the columns stay the same when a route changes
    const routesRef = useRef(routes)
    routesRef.current = routes

    useEffect(() => {
        getVehicles()
    }, [getVehicles])

    // Selected routes that are gone (other dates, removed) can't be acted on
    useEffect(() => {
        setSelectedRoutes((selectedRoutes) => {
            const stillThere = selectedRoutes.filter((id) => routes[id])
            return stillThere.length === selectedRoutes.length ? selectedRoutes : stillThere
        })
    }, [routes])

    const rows = useMemo(() => {
        return Object.values(routes || {}).map((route) => getRouteRow(route, {reseller, vehicles, usersById: usersObject || {}}))
    }, [routes, reseller, vehicles, usersObject])

    // The planning of the route's day with the route focused (see Dashboard), on the tab of the route's state
    const onClickRoute = useCallback((row: RouteRow) => {
        const route = routesRef.current[row._id]

        if (!route) {
            return
        }

        OrderActions.setActiveRouteTab(route.completed ? 'completed' : 'open')
        history.push(`/planning?view=planning&startDate=${route.date}&endDate=${route.date}&routeId=${route._id}`)
    }, [history])

    const columns = useMemo<TableColumn<RouteRow>[]>(() => {
        const columns: TableColumn<RouteRow>[] = [
            ...branchesAvailable(reseller) ?
                [{id: 'branchName', title: t('branch'), visible: false, key: 'branchName', width: 180}] :
                [],
            {id: 'name', title: t('name'), visible: true, key: 'name', width: 200},
            {id: 'date', title: t('date'), visible: true, key: 'date', width: 110, text: (row) => moment(row.date).format('DD-MM-YYYY'), sort: (row) => row.date},
            {id: 'status', title: t('status'), visible: true, key: 'status', width: 120, text: (row) => row.status, render: (row) => <RouteStatusBadge status={row.statusKey} />, sort: (row) => ['concept', 'ready', 'started', 'completed'].indexOf(row.statusKey)},
            {id: 'messenger', title: t('messenger'), visible: true, key: 'messenger', width: 180},
            {id: 'vehicle', title: t('vehicle'), visible: true, key: 'vehicle', width: 150},
            {id: 'vehicleType', title: t('vehicleType'), visible: false, key: 'vehicleType', width: 150},
            {id: 'startTimePlanned', title: plannedTitle(t('startTime')), visible: true, key: 'startTimePlanned', width: 90, sort: (row) => timeSortValue(row.startTimePlanned)},
            {id: 'startTimeExecuted', title: executedTitle(t('startTime')), visible: true, key: 'startTimeExecuted', width: 90, sort: (row) => timeSortValue(row.startTimeExecuted)},
            {id: 'endTimePlanned', title: plannedTitle(t('endTime')), visible: true, key: 'endTimePlanned', width: 90, sort: (row) => timeSortValue(row.endTimePlanned)},
            {id: 'endTimeExecuted', title: executedTitle(t('endTime')), visible: true, key: 'endTimeExecuted', width: 90, sort: (row) => timeSortValue(row.endTimeExecuted)},
            {id: 'stops', title: t('stops'), visible: true, key: 'stops', width: 80},
            {id: 'colli', title: t('colli'), visible: true, key: 'colli', width: 80},
            {id: 'distancePlanned', title: plannedTitle(t('distance')), visible: true, key: 'distancePlanned', width: 100, sort: (row) => row.distancePlannedMeters || undefined},
            {id: 'distanceExecuted', title: executedTitle(t('distance')), visible: true, key: 'distanceExecuted', width: 100, sort: (row) => row.distanceExecutedMeters || undefined},
            {id: 'durationPlanned', title: plannedTitle(t('duration')), visible: true, key: 'durationPlanned', width: 90, sort: (row) => row.durationPlannedSeconds || undefined},
            {id: 'durationExecuted', title: executedTitle(t('duration')), visible: true, key: 'durationExecuted', width: 90, sort: (row) => row.durationExecutedSeconds || undefined},
            {id: 'revenue', title: t('revenue'), visible: false, key: 'revenue', width: 100, text: (row) => `€ ${row.revenue}`, sort: (row) => row.revenueValue},
            {id: 'revenuePerHour', title: t('revenuePerHour'), visible: false, key: 'revenuePerHour', width: 120, text: (row) => `€ ${row.revenuePerHour}`, sort: (row) => row.revenuePerHourValue}
        ]

        // Clicking a row opens the route, the checkboxes select
        return columns.map((column) => ({...column, onClick: onClickRoute}))
    }, [reseller, onClickRoute])

    // Searched on what the columns show, not on ids or other data of the row
    const filteredRows = useMemo(() => filter.byColumns(rows, queries, columns) as RouteRow[], [rows, queries, columns])

    const getSelected = () => selectedRoutes.map((id) => routes[id]).filter(Boolean)

    // The bulk actions of the Dagplanning, on the selected routes it can be done on
    const onClickBulkAction = async (action: RouteBulkAction) => {
        const done = await runRouteBulkAction(action, getSelected(), {
            popup: popupRef.current,
            getRoute: (routeId) => routesRef.current[routeId],
            onStart: () => setBulkRunning(true)
        })

        if (done) {
            setBulkRunning(false)
            setSelectedRoutes([])
        }
    }

    const withBranch = branchesAvailable(reseller)
    const exportColumns = getRouteExportColumns(withBranch)

    // With the template chosen in the export modal, like the export of the Ritten
    const onExport = async (template: {id?: string, name: string, columns: string[]}, onProgress: (now: number, max: number) => void) => {
        const ids = new Set(selectedRoutes)
        const exportRows = filteredRows.filter((row) => ids.has(row._id))

        await csvExport.routes(exportRows, orderStartDate, orderEndDate, withBranch, template, onProgress)

        setSelectedRoutes([])
        setExportOpen(false)
    }

    const selected = getSelected()

    return (
        <Stack style={{flex: 1}}>
            <Panel style={{display: 'flex', borderTop: 'none', borderLeft: 'none', borderRight: 'none', background: Colors.backgroundNeutral, padding: '20px 10px 10px 10px'}}>
                <SearchBar
                    columns={columns.filter((column) => !!column.title)}
                    queries={queries}
                    onQueryChange={(queries) => {
                        tableRef.current?.selectPage(1)
                        setQueries([...queries])
                        setSelectedRoutes([])
                    }}
                    // The dates of the OrderStore, like the Ritten view: the planning shows the first day of them
                    onDateChange={(startDate, endDate) => OrderActions.setDates(startDate, endDate)}
                    startDate={orderStartDate}
                    endDate={orderEndDate}
                    dateRange
                />

                <Row style={{paddingLeft: 6, paddingRight: 6, flex: 'none', columnGap: formSpacing.columnGap, height: 36, marginBottom: 6, minWidth: 'auto'}}>
                    <Button
                        variant='outline-white'
                        style={{flexShrink: 0}}
                        buttonStyle={{whiteSpace: 'nowrap'}}
                        icon='mdi mdi-plus'
                        label={t('newRoute')}
                        onClick={() => editRouteModalRef.current?.open()}
                    />

                    {selectedRoutes.length > 0 &&
                        <>
                            <Button
                                variant='outline-white'
                                onClick={() => setExportOpen(true)}
                                tooltip={t('export')}
                                icon='mdi mdi-file-export'
                            />

                            {tableBulkActions.map((action) => (
                                <Button
                                    key={action}
                                    variant='outline-white'
                                    onClick={() => onClickBulkAction(action)}
                                    disabled={bulkRunning || !selected.some((route) => !getRouteBulkActionDisabledReason(action, route))}
                                    tooltip={t(routeBulkActionOptions[action].label)}
                                    icon={routeBulkActionOptions[action].icon}
                                />
                            ))}
                        </>
                    }
                </Row>
            </Panel>

            <div style={{flex: 1, marginTop: 24, marginLeft: 24, marginRight: 24}}>
                <Table
                    ref={tableRef}
                    tableName='routes'
                    columns={columns}
                    rows={filteredRows}
                    selectedRows={selectedRoutes}
                    loading={ordersLoading}
                    onChange={setSelectedRoutes}
                    defaultSort={{column: 'startTimePlanned'}}
                />
            </div>

            <Popup ref={popupRef} />

            <ExportTemplateModal
                title={t('exportRoutes')}
                open={exportOpen}
                onClose={() => setExportOpen(false)}
                templates={reseller.settings.routes?.exportCsvTemplates || []}
                onSaveTemplates={(templates) => {
                    reseller.settings.routes = {...reseller.settings.routes, exportCsvTemplates: templates}
                    onUpdateReseller(reseller, 'routes.exportCsvTemplates')
                }}
                methods={[{value: 'route', title: t('exportPerRoute')}]}
                getColumns={() => exportColumns}
                defaultColumns={exportColumns.map((column) => column.value)}
                newTemplate={newRouteExportTemplate}
                onClickExport={onExport}
            />
            <EditRoute reseller={reseller} users={usersObject || {}} ref={editRouteModalRef} />
        </Stack>
    )
}

class RoutesTableWrapper extends Reflux.Component {
    constructor(props) {
        super(props)

        // @ts-expect-error reflux-react-16 is not typed
        this.stores = [OrderStore, UserStore]
        // @ts-expect-error reflux-react-16 is not typed
        this.storeKeys = ['routes', 'orderStartDate', 'orderEndDate', 'ordersLoading', 'usersObject']
    }

    render() {
        // @ts-expect-error reflux-react-16 is not typed
        return <RoutesTable {...this.props} {...this.state} />
    }
}

// Typed for the TSX that renders it, Reflux.Component isn't
export default RoutesTableWrapper as unknown as React.ComponentType<Pick<RoutesTableProps, 'reseller' | 'onUpdateReseller' | 'history'>>
